import React, { useEffect, useState } from "react";
import Button from "src/components/common/Button";
import styles from "./styles.module.scss";

// How often to check whether a newer UI build has been deployed.
const POLL_MS = 60 * 1000;

/**
 * Shows a non-dismissable banner when the served UI build differs from the one
 * this tab is running, so the user can reload at a moment of their choosing
 * instead of silently running a stale build (or hitting a 404'd lazy chunk
 * after a deploy). The only action is Reload; the banner never reloads on its
 * own, so in-progress work is not lost.
 */
const UpdateBanner = () => {
    const [updateAvailable, setUpdateAvailable] = useState(false);

    useEffect(() => {
        const bootHash = window.__GIT_HASH__;
        let stopped = false;
        let intervalId;

        const markUpdate = () => {
            if (stopped) return;
            stopped = true;
            clearInterval(intervalId);
            setUpdateAvailable(true);
        };

        const check = async () => {
            try {
                const resp = await fetch(`/version.json?_=${Date.now()}`, {
                    cache: "no-store",
                });
                if (!resp.ok) return;
                const { gitHash } = await resp.json();
                // Only prompt when we can positively confirm a different build;
                // a missing hash on either side is treated as "unknown", not new.
                if (bootHash && gitHash && gitHash !== bootHash) {
                    markUpdate();
                }
            } catch (e) {
                // Transient (e.g. ingress rolling during a deploy): try next tick.
            }
        };

        // A failed lazy-chunk load means the served build changed under this tab
        // (old chunk hashes 404 on the new pod). Surface the prompt immediately
        // rather than wait for the next poll.
        window.addEventListener("app:update-available", markUpdate);

        check();
        intervalId = setInterval(check, POLL_MS);

        return () => {
            stopped = true;
            clearInterval(intervalId);
            window.removeEventListener("app:update-available", markUpdate);
        };
    }, []);

    if (!updateAvailable) return null;

    return (
        <div className={styles.banner} data-testid="new-version-banner">
            <span className={styles.message}>
                A new version of Tru is available.
            </span>
            <Button
                value="Reload"
                onClick={() => window.location.reload()}
                dataTestId="reload-new-version"
            />
        </div>
    );
};

export default UpdateBanner;
